import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PagesManager from './PagesManager';
import PhotoBookPreview from './PhotoBookPreview';
import { DEFAULT_COVER_DATA, CoverControls } from './CoverEditor';
import { generateOrderPDF } from '../../services/pdfExportService';
import { storePhotoBookData, savePhotoBookToIDB, loadPhotoBookFromIDB } from '../../utils/imageStorage';
import Header from '../Header';
import { useLanguage } from '../../context/LanguageContext';
import './PhotoBookDesigner.css';
import './PhotoBookTheme.css';

// IDs e produkteve që kanë cover personalizim (nga Supabase)
const COVER_EDITOR_PRODUCT_IDS = [124];

const PhotoBookDesigner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const { t } = useLanguage();

  // productData vjen nga location.state ose restaurohet nga IDB pas refresh
  const [productData, setProductData] = useState(location.state);

  // hasCoverEditor: kontrollohet nga ID e produktit (URL) DHE emri
  // Kjo funksionon edhe kur productData nuk është ngarkuar ende (refresh/URL direkt)
  const hasCoverEditor = useMemo(() => {
    const byId   = COVER_EDITOR_PRODUCT_IDS.includes(parseInt(routeId));
    const byName = /personalizo|kopertin/i.test(productData?.name || '');
    return byId || byName;
  }, [routeId, productData?.name]);

  // Cover data
  const [coverData, setCoverData] = useState(DEFAULT_COVER_DATA);

  // Track whether the cover page is active (to show controls in sidebar)
  // Fillimisht aktive nëse produkti ka cover editor
  const [isCoverActive, setIsCoverActive] = useState(hasCoverEditor);

  // Mobile: sidebar/photo-library/cover-controls live in a full-screen
  // drawer so the canvas + toolbar + thumbnails stay static underneath
  // (no page scroll needed) until the customer explicitly opens it.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Desktop: cover controls live in a slide-over panel opened by a button
  // (instead of being stacked in the sidebar).
  const [coverPanelOpen, setCoverPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1100px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1100px)');
    const handleChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);
  // Portal target inside the mobile drawer where PagesManager renders its
  // photo-library/page-info content (kept in PagesManager so its state/
  // handlers stay co-located; only its DOM location moves on mobile).
  const [pagesPortalNode, setPagesPortalNode] = useState(null);

  // Sinkronizo isCoverActive kur hasCoverEditor ndryshohet (p.sh. pas ngarkimit të IDB)
  useEffect(() => {
    if (hasCoverEditor) setIsCoverActive(true);
  }, [hasCoverEditor]);

  // Refs for PDF export
  const pageRefs = useRef([]);
  const saveTimerRef = useRef(null);

  // Pages data
  const [pages, setPages] = useState([
    {
      id: 1,
      layout: 'single',
      backgroundColor: '#ffffff',
      images: {}
    },
    {
      id: 2,
      layout: 'two-horizontal',
      backgroundColor: '#ffffff',
      images: {}
    }
  ]);

  // Uploaded photos library
  const [uploadedPhotos, setUploadedPhotos] = useState([]);

  // Restauro nga IndexedDB kur faqja u rifresku.
  // Përdorim sessionStorage flag (i besueshëm në mobile) në vend të performance.navigation
  // që nuk funksionon mirë në iOS Safari dhe browser-a mobile.
  useEffect(() => {
    const isNewSession = sessionStorage.getItem('pb_new_session');
    if (isNewSession) {
      // Navigim i ri nga ProductDetail – mos restauro IDB, fillo nga zeroja
      sessionStorage.removeItem('pb_new_session');
      return;
    }
    // Nuk ka flag → mund të jetë refresh → provo IDB
    loadPhotoBookFromIDB().then(data => {
      if (!data) return;
      setProductData(prev => prev || data.productData);
      setUploadedPhotos(data.uploadedPhotos);
      setPages(data.pages);
      if (data.coverData) setCoverData(data.coverData);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ruaj MENJËHERË kur ngarkohen foto të reja (kriteri më i rëndësishëm)
  useEffect(() => {
    if (!productData || uploadedPhotos.length === 0) return;
    savePhotoBookToIDB({ uploadedPhotos, pages, productData, coverData });
  }, [uploadedPhotos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ruaj me vonesë kur ndryshojnë faqet (layout, zoom, pan)
  useEffect(() => {
    if (!productData || uploadedPhotos.length === 0) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      savePhotoBookToIDB({ uploadedPhotos, pages, productData, coverData });
    }, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [pages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ruaj emergjent para refresh/mbyllje.
  // 'pagehide' është i besueshëm në iOS Safari ku 'beforeunload' nuk funksionon.
  useEffect(() => {
    const handleSave = () => {
      if (uploadedPhotos.length > 0 && productData) {
        savePhotoBookToIDB({ uploadedPhotos, pages, productData, coverData });
      }
    };
    window.addEventListener('pagehide', handleSave);
    window.addEventListener('beforeunload', handleSave);
    return () => {
      window.removeEventListener('pagehide', handleSave);
      window.removeEventListener('beforeunload', handleSave);
    };
  }, [uploadedPhotos, pages, productData]);

  // Export/Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ step: 0, total: 0, message: '' });
  const [previewOpen, setPreviewOpen] = useState(false);

  // Add photos to library
  const handleAddPhotos = useCallback((newPhotos) => {
    setUploadedPhotos(prev => [...prev, ...newPhotos]);
  }, []);

  // Remove photo from library and clean up blob URL to free memory
  const handleRemovePhoto = useCallback((photoId) => {
    setUploadedPhotos(prev => {
      const photoToRemove = prev.find(p => p.id === photoId);
      // Revoke blob URL to free memory
      if (photoToRemove?.src?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(photoToRemove.src);
        } catch (e) {
          // Ignore errors
        }
      }
      return prev.filter(p => p.id !== photoId);
    });
  }, []);

  // Calculate max pages based on selected option
  const getMaxPages = () => {
    const selectedPages = productData?.selectedPages;
    if (selectedPages?.pages >= 100) {
      return 115;
    }
    return 77;
  };

  // Calculate max photos based on selected option
  const getMaxPhotos = () => {
    if (productData?.selectedPages?.maxPhotos) {
      return productData.selectedPages.maxPhotos;
    }
    const selectedPages = productData?.selectedPages;
    if (selectedPages?.pages >= 100) {
      return 150;
    }
    return 75;
  };

  // Proceed to order form - generates PDF and passes it along
  const handleProceedToOrder = async () => {
    // Validate that there are photos in pages
    const totalPhotosInPages = pages.reduce((count, page) => count + Object.keys(page.images).length, 0);
    
    if (totalPhotosInPages === 0) {
      alert(t('designer.alert.noPhotosInPages'));
      return;
    }

    // Photos can be uploaded to the library without ever being dragged/placed
    // onto a page slot - those would otherwise be silently excluded from the
    // printed book with no warning. List them by name so the customer can
    // either place them or knowingly leave them out.
    const placedPhotoIds = new Set();
    pages.forEach(page => {
      Object.values(page.images || {}).forEach(img => { if (img?.id) placedPhotoIds.add(img.id); });
    });
    const unplacedPhotos = uploadedPhotos.filter(p => !placedPhotoIds.has(p.id));
    if (unplacedPhotos.length > 0) {
      alert(t('designer.alert.unplaced', {
        count: unplacedPhotos.length,
        names: unplacedPhotos.map(p => `• ${p.name}`).join('\n'),
      }));
      return;
    }

    setIsProcessing(true);
    setProcessingProgress({ step: 0, total: 0, message: t('designer.preparing') });

    try {
      const totalPrice = productData?.totalPrice || productData?.price || 24.90;

      storePhotoBookData({
        pages: pages,
        uploadedPhotos: uploadedPhotos,
        product: productData,
        coverData: hasCoverEditor ? coverData : null,
        totalPrice,
        photosInPages: totalPhotosInPages
      });

      navigate('/order', {
        state: {
          product: productData,
          selectedPages: { pages: pages.length, label: `${pages.length} ${t('designer.pageWord')}` },
          totalPrice,
          isPhotoBook: true,
          photosInPages: totalPhotosInPages,
          hasCoverPersonalization: hasCoverEditor,
          usePhotoBookStorage: true
        }
      });
    } catch (error) {
      console.error('Error preparing order:', error);
      alert(t('designer.alert.genericError', { message: error.message }));
    } finally {
      setIsProcessing(false);
    }
  };

  // Get total number of photos in pages
  const getTotalPhotos = () => {
    let count = 0;
    pages.forEach(page => {
      count += Object.keys(page.images).length;
    });
    return count;
  };

  // Page stats block — rendered both in the desktop sidebar and (mobile)
  // inside the full-screen drawer, so it's defined once and reused.
  const pageStats = (!hasCoverEditor || !isCoverActive) && (
    <>
      <div className="page-info-box">
        <h4>{t('designer.pageLimits')}</h4>
        <p>
          {uploadedPhotos.length < 5
            ? t('designer.uploadMore', { n: uploadedPhotos.length })
            : t('designer.uploadedCount', { count: uploadedPhotos.length, max: getMaxPhotos() })}
        </p>
        <p className="max-pages">{t('designer.maxLine', { maxPhotos: getMaxPhotos(), maxPages: getMaxPages() })}</p>
      </div>
      <div className="design-stats">
        <div className="stat">
          <span className="stat-value">{pages.length}</span>
          <span className="stat-label">{t('designer.statPages')}</span>
        </div>
        <div className="stat">
          <span className="stat-value">{getTotalPhotos()}</span>
          <span className="stat-label">{t('designer.statPhotosInPages')}</span>
        </div>
        <div className="stat">
          <span className="stat-value">{uploadedPhotos.length}</span>
          <span className="stat-label">{t('designer.statUploaded')}</span>
        </div>
      </div>
    </>
  );

  return (
    <div className="photobook-designer">
      <Header />

      {/* Mobile drawer toggle — opens the full-screen panel below */}
      <button
        type="button"
        className="drawer-toggle-tab"
        onClick={() => setDrawerOpen(true)}
      >
        <span className="drawer-toggle-arrow">☰</span>
        <span className="drawer-toggle-label">{t('designer.openDrawer')}</span>
      </button>

      {/* Full-screen mobile drawer — product info, stats, photo library
          (or cover controls) all in one scrollable page, closed by default
          so the canvas below is what the customer sees immediately. */}
      <div className={`mobile-drawer${drawerOpen ? ' open' : ''}`}>
        <div className="mobile-drawer-header">
          <div>
            <h3>{productData?.displayName || productData?.name || t('designer.defaultTitle')}</h3>
            {productData && (
              <span className="mobile-drawer-price">€{productData.totalPrice || productData.price || 0}</span>
            )}
          </div>
          <button
            type="button"
            className="mobile-drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label={t('designer.closeDrawer')}
          >×</button>
        </div>
        <div className="mobile-drawer-body">
          {pageStats}
          {hasCoverEditor && isCoverActive ? (
            <CoverControls coverData={coverData} onUpdate={setCoverData} onCloseDrawer={() => setDrawerOpen(false)} />
          ) : (
            <div ref={setPagesPortalNode} className="drawer-pages-slot" />
          )}
        </div>
      </div>

      {/* Desktop cover slide-over. Kept mounted (just hidden) so the tab,
          sticker list and scroll position survive closing/reopening. */}
      {hasCoverEditor && !isMobile && (
        <aside
          className={`cover-slideover${coverPanelOpen && isCoverActive ? ' open' : ''}`}
          aria-hidden={!(coverPanelOpen && isCoverActive)}
        >
          <div className="cover-slideover-header">
            <h3>{t('designer.customizeCover')}</h3>
            <button
              type="button"
              className="cover-slideover-close"
              onClick={() => setCoverPanelOpen(false)}
              aria-label={t('designer.closeDrawer')}
            >×</button>
          </div>
          <div className="cover-slideover-body">
            <CoverControls coverData={coverData} onUpdate={setCoverData} />
          </div>
        </aside>
      )}

      <div className="designer-container">
        {/* Sidebar — desktop only; on mobile its content lives in the
            drawer above, but .export-section (the fixed proceed bar)
            stays here so it keeps its own fixed positioning untouched. */}
        <div className="designer-sidebar">
          <div className="designer-product-info">
            {productData && (
              <>
                <h2>{productData.displayName || productData.name}</h2>
                <p className="designer-product-price">
                  €{productData.totalPrice || productData.price || 0}
                </p>
              </>
            )}
            {!productData && <h2>{t('designer.defaultTitle')}</h2>}
          </div>

          {/* Cover controls — desktop: button opens a slide-over panel */}
          {hasCoverEditor && isCoverActive && !isMobile && (
            <button
              type="button"
              className="cover-panel-open-btn"
              onClick={() => setCoverPanelOpen(true)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 6h8M18 6h2M4 12h2M12 12h8M4 18h10M20 18h0" />
                <circle cx="15" cy="6" r="2" /><circle cx="9" cy="12" r="2" /><circle cx="17" cy="18" r="2" />
              </svg>
              <span>{t('designer.customizeCover')}</span>
            </button>
          )}

          {/* Page stats — shown when pages are active */}
          {pageStats}

          {/* Proceed section */}
          <div className="export-section">
            {isProcessing && (
              <div className="export-progress">
                <div
                  className="progress-bar"
                  style={{ width: `${(processingProgress.step / processingProgress.total) * 100}%` }}
                ></div>
                <span>{processingProgress.message}</span>
              </div>
            )}

            <button
              type="button"
              className="preview-toggle-btn"
              onClick={() => setPreviewOpen(true)}
              disabled={isProcessing || uploadedPhotos.length < 5}
            >
              {t('designer.previewBtn')}
            </button>

            <button
              className="proceed-btn"
              onClick={handleProceedToOrder}
              disabled={isProcessing || uploadedPhotos.length < 5}
            >
              {isProcessing ? (
                <>
                  <span className="spinner"></span>
                  {t('designer.processing')}
                </>
              ) : (
                <>{t('designer.continueOrder')}</>
              )}
            </button>

            {uploadedPhotos.length < 5 && (
              <p className="min-photos-warning">
                {t('designer.minPhotosWarning')}
              </p>
            )}
          </div>

          {/* Help info */}
          <div className="help-info">
            <h4>{t('designer.helpTitle')}</h4>
            <ul>
              <li>{t('designer.help1')}</li>
              <li>{t('designer.help2')}</li>
              <li>{t('designer.help3')}</li>
              <li>{t('designer.help4')}</li>
            </ul>
          </div>
        </div>

        {/* Main design area — cover + pages unified */}
        <div className="designer-main">
          <PagesManager
            pages={pages}
            onUpdate={setPages}
            uploadedPhotos={uploadedPhotos}
            onAddPhotos={handleAddPhotos}
            onRemovePhoto={handleRemovePhoto}
            pageRefs={pageRefs}
            maxPages={getMaxPages()}
            maxPhotos={getMaxPhotos()}
            coverData={hasCoverEditor ? coverData : null}
            onCoverUpdate={hasCoverEditor ? setCoverData : null}
            onActivePageChange={hasCoverEditor
              ? (section => setIsCoverActive(section === 'cover'))
              : undefined}
            pagesPortalTarget={isMobile ? pagesPortalNode : null}
            onCloseDrawer={() => setDrawerOpen(false)}
            onOpenDrawer={() => (isMobile ? setDrawerOpen(true) : setCoverPanelOpen(true))}
          />
        </div>
      </div>

      {previewOpen && (
        <PhotoBookPreview
          pages={pages}
          onClose={() => setPreviewOpen(false)}
          onProceedToOrder={handleProceedToOrder}
        />
      )}
    </div>
  );
};

export default PhotoBookDesigner;
